import {Component, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {SpThemingService} from '../../../../services/sp-theming/sp-theming.service';
import {MatSort} from '@angular/material/sort';
import {ApiPlanService} from '../../../../services/sp-api/sp-api-plan/api-plan.service';
import {Plan} from '../../../../models/plan';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Subscription} from '../../../../models/subscription';
import {Company} from '../../../../models/company';
import {ApiCompanyService} from '../../../../services/sp-api/sp-api-company/api-company.service';
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../../../models/user';
import {Role} from '../../../../models/role';
import {ApiRoleService} from '../../../../services/sp-api/sp-api-role/api-role.service';
import {environment} from '../../../../../environments/environment';
import {Message} from 'primeng/api';
import _ from 'lodash';
import {LoaderService} from '../../../../services/sp-loading/loader.service';
import {AlertService} from '../../../../services/sp-alert/alert.service';
import {TrackerService} from '../../../../services/sp-tracker/tracker.service';
import { RoleUtils } from 'src/app/utils/role/role-utils';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';

@Component({
  selector: 'app-sp-admin-company-create',
  templateUrl: './sp-admin-company-create.component.html',
  styleUrls: ['./sp-admin-company-create.component.scss']
})

export class SpAdminCompanyCreateComponent implements OnInit {
  env = environment;
  plans: Array<Plan> = [];
  selectedPlan: Plan = null;
  customPlan: Plan = new Plan("Custom Plan", 0, 0, []);

  userListDisplayedColumns: string[] = [
    'image',
    'last_name',
    'email',
    'type',
    'actions',
  ];

  paymentOptions: {value: string, label: string}[] = [
    {value: "On demand", label: "One time payment"},
    {value: "On license", label: "Monthly payment"},
    {value: "Free", label: "Free user subscription"},
    {value: "Trial", label: "Trial user subscription"}
  ];

  msgs: Message[] = [];
  isNotCreated = true;
  editMode = false;
  value = 0;

  monthlyCredits = 0;
  nbrUsers = 0;
  extraCredits = 0;
  extraCreditsModified = false;

  userDataSource = new MatTableDataSource<User>([]);
  roles: Array<Role> = [];

  selectedImage: any = null;

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  companyFormGroup: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(50)
    ]),
    paymentFrequency: new UntypedFormControl('', [
      Validators.required
    ]),
    audienseId: new UntypedFormControl(''),
  });

  subscriptionStartDate = new Date();
  subscriptionEndDate = new Date();

  // Permissions
  canManageCompany: boolean = false;
  isCompanyAdmin: boolean = false;
  isUserAdmin: boolean = false;
  isUserSuperAdmin: boolean = false;
  // Url
  backUrl: string;

  // Agency management
  protected companyDataSource: MatTableDataSource<Company> = new MatTableDataSource<Company>([]);
  protected companyManagedDisplayColumns: string[] = ['id', 'name', 'actions'];
  protected allCompanies: Company[] = [];
  protected allCompaniesFiltered: Company[] = [];
  protected selectedCompany?: Company;

  // Constructor.
  constructor(private _spTheming: SpThemingService,
              private apiPlan: ApiPlanService,
              private apiCompany: ApiCompanyService,
              private apiRole: ApiRoleService,
              private router: Router,
              private loader: LoaderService,
              private route: ActivatedRoute,
              private alert: AlertService,
              private tracker: TrackerService,
              protected sessionProvider: SessionProviderService) {
    // Set the theme to use.
    this._spTheming.setSpThemeName('dashboard-desk');
  }

  createdCompany: Company = null;

  // On init.
  async ngOnInit() {
    this.loader.load();

    this.sessionProvider.activeSession?.hasPermission('company.self.manage').then(authorization => {
      this.canManageCompany = authorization;
    });

    this.sessionProvider.activeSession?.hasPermission('user.admin').then(authorization => {
      this.isUserAdmin = authorization;
    });

    this.isCompanyAdmin = await this.sessionProvider.activeSession?.hasPermission('company.admin');

    this.apiPlan.getAll().then((plans: Plan[]) => {
      this.plans = plans;
      this.apiRole.getAll().then((roles: Role[]) => {
        this.roles = roles;

        // When all components are loaded, getting company if existing
        this.getCompanyFromRoute();
      });
    });

    this.userDataSource.sort = this.sort;
    this.userDataSource.paginator = this.paginator;

    this.sessionProvider.activeSession?.hasPermission('company.admin').then(authorization => {
      if (!authorization) {
        this.companyFormGroup.get('paymentFrequency').disable();
        this.backUrl = '/';
      } else {
        this.backUrl = '/admin/companies';
      }
    });
    this.sessionProvider.activeSession?.hasPermission('company.self.manage').then(authorization => {
      if (!authorization) {
        this.companyFormGroup.disable();
        this.userListDisplayedColumns = this.userListDisplayedColumns.filter(c => c !== "actions");
      }
    });

    if (this.isCompanyAdmin) {
      this.apiCompany.getAll({min: true}).then((companies: Company[]) => {
        this.allCompanies = companies;
        this.allCompaniesFiltered = companies;
      });
    }
    if (this.sessionProvider.activeSession?.user && this.sessionProvider.activeSession.user.role.level >= RoleUtils.superAdminLevel) this.isUserSuperAdmin = true;
  }
  showSuccess() {
    this.msgs = [];
    this.msgs.push({severity:'success', summary:'Success Message', detail:'The company has been ' + (this.editMode ? 'updated' : 'created') + ' successfully'});
  }
  modifyMonthlyCredits(value?: number) {
    if (value) {
      this.monthlyCredits += value;
      if (this.monthlyCredits < 0) this.monthlyCredits = 0;
    }

    this.onPlanInfoChange();
  }
  modifyUsers(value?: number) {
    if(value) {
      this.nbrUsers += value;
      if (this.nbrUsers < 0) this.nbrUsers = 0;
    }

    this.onPlanInfoChange();
  }
  modifyExtraCredits(value?: number) {
    if(value) {
      this.extraCredits += value;
      this.extraCreditsModified = true;
      if (this.extraCredits < 0) this.extraCredits = 0;
    }
  }
  onPlanInfoChange() {
    if (!this.selectedPlan) return;
    if (this.selectedPlan !== this.customPlan) {
      const previousPlanPerms = this.selectedPlan.permissions;
      const previousPlanIsFreeOrTrial = this.selectedPlan.name == "Free" || this.selectedPlan.name == "Trial";
      this.selectedPlan = this.customPlan;

      const proPlan = this.plans.find(p => p.name == "Pro");
      if (previousPlanIsFreeOrTrial && proPlan) {
        this.customPlan.permissions = proPlan.permissions;
      } else {
        this.customPlan.permissions = previousPlanPerms;
      }
    }

    this.customPlan.credits = this.monthlyCredits;
    this.customPlan.users = this.nbrUsers;
  }
  onPaymentInfoChange(value: string) {
    if (value === 'Trial') {

      this.selectedPlan = this.plans.find(p => p.name == 'Trial') || null;
      if (this.selectedPlan) this.onPlanSelectChange();
    }
  }
  getCompanyFromRoute() {
    this.route.paramMap.subscribe(params => {
      let companyId = params.get('company_id');

      if(companyId) {
        this.loader.load();
        this.apiCompany.getOne(parseInt(companyId)).then(async company => {
          this.loadCompanyInfo(company);
          this.companyDataSource.data = await this.apiCompany.getManagedCompanies(this.createdCompany);
        }, error => {
          if (error.status == 404) {
            this.router.navigate(['page-not-found'])
          } else if (error.status == 403) {
            this.router.navigate(['page-access-denied'])
          }
        }).finally(() => this.loader.loaded());
      } else {
        this.loader.loaded();
      }
    })
  }
  loadCompanyInfo(company: Company) {
    this.companyFormGroup.get('name').setValue(company.name);

    this.companyFormGroup.get('paymentFrequency').setValue(company.subscription.payment_frequency);
    this.subscriptionStartDate = company.subscription.contract_start_at;
    this.subscriptionEndDate = company.subscription.contract_expire_at;

    this.monthlyCredits = company.subscription.plan.credits;
    this.extraCredits = company.subscription.credits_extra;
    this.nbrUsers = company.subscription.users;

   this.companyFormGroup.get('audienseId').setValue(company.audiense_id);

    if(company.subscription.plan.custom) {
      this.customPlan = company.subscription.plan;
      this.selectedPlan = this.customPlan;
    } else {
      //this.customPlan = null;
      this.selectedPlan = this.plans.find(p => p.id == company.subscription.plan.id);
    }

    this.selectedImage = company.image === null ? null : this.env.config.api.storageUrl + company.image;

    this.createdCompany = company;
    this.userDataSource.data = this.createdCompany.users;

    this.isNotCreated = false;
    this.editMode = true;
  }
  onPlanSelectChange() {
    if (this.selectedPlan !== null) {
      this.nbrUsers = this.selectedPlan.users;
      this.monthlyCredits = this.selectedPlan.credits;
    } else {
      this.nbrUsers = 0;
      this.monthlyCredits = 0;
    }
  }
  companyCreate() {
    if (!this.validateInfo() || this.createdCompany !== null) return;

    let company = this.buildCompanyFromInfo();

    this.apiCompany.postOne(company).then(company => {
      this.tracker.track('admin_company_created', {company: company.name});
      this.router.navigateByUrl('admin/companies/update/' + company.id).then();
    }).catch(error => {
      this.handleValidationErrors(error);
    })
  }
  companyUpdate() {
    if (!this.validateInfo() || this.createdCompany === null) return;

    let company = this.buildCompanyFromInfo(this.createdCompany.id);

    this.apiCompany.updateOne(company).then(company => {
      this.sendExtraCreditsModifiedEvent();

      // Resetting custom plan by creating a new one and clearing old incorrect plan ID
      if (!company.subscription.plan.custom) this.customPlan = new Plan("Custom plan", 0, 0, []);
      this.loadCompanyInfo(company);
      this.showSuccess();
    }).catch(error => {
      this.handleValidationErrors(error);
    })
  }

  sendExtraCreditsModifiedEvent(){
    if (!this.extraCreditsModified) return;
    
    this.tracker.track('subscription_extra_credits_modified', {company_name: this.createdCompany.name, new_extra_credits: this.extraCredits});
    this.extraCreditsModified = false;
  }

  companySubscriptionBlock() {
    const newSubEndDate = new Date();
    newSubEndDate.setHours(1);
    if (newSubEndDate.getMonth() == 0) {
       newSubEndDate.setFullYear(newSubEndDate.getFullYear() - 1);
       newSubEndDate.setMonth(11);
    } else {
      newSubEndDate.setMonth(newSubEndDate.getMonth() - 1);
    }
    this.createdCompany.subscription.contract_expire_at = newSubEndDate;
    this.apiCompany.updateOne(this.createdCompany).then(company => {
      this.alert.notify('Success', 'The company subscription has been set to expiration !', 'success');
      this.loadCompanyInfo(company);
    });
  }
  //TODO: Optimize with model instead of variables ?
  buildCompanyFromInfo(companyId?: number) {
    let buildCompany: Company = new Company("");
    if(companyId) buildCompany.id = companyId;

    let startDate: Date = new Date(this.subscriptionStartDate);
    startDate.setHours(2); // Adaptation for database | TODO : See for real option with timezone support
    let endDate = new Date(this.subscriptionEndDate);
    endDate.setHours(2);
    buildCompany.name = this.companyFormGroup.get('name').value.charAt(0).toUpperCase() + this.companyFormGroup.get('name').value.slice(1);
    buildCompany.audiense_id = this.companyFormGroup.get('audienseId').value;

    let subscription = new Subscription(
      this.monthlyCredits,
      this.nbrUsers,
      this.companyFormGroup.get('paymentFrequency').value,
      startDate,
      endDate
    );
    subscription.credits_extra = this.extraCredits;

    if (this.selectedPlan !== null) {
      subscription.plan = _.cloneDeep(this.selectedPlan); // Avoiding by ref value view artifacts (numbers blinking)

      if (this.selectedPlan.custom) {
        subscription.plan.credits = this.monthlyCredits;
        subscription.plan.users = this.nbrUsers;
      }
    } else {
      // Default permissions are set in the backend if not filled here
      subscription.plan = new Plan("Custom plan", this.monthlyCredits, this.nbrUsers, []);
    }

    buildCompany.subscription = subscription;
    buildCompany.image = this.selectedImage;
    return buildCompany;
  }

  validateInfo(): boolean {
    if (!this.sessionProvider.activeSession?.hasPermission('company.admin')) {
      return this.companyFormGroup.valid
    } else {
      return this.companyFormGroup.valid && !!this.subscriptionStartDate && !!this.subscriptionEndDate
    }
  }

  onImageChange(imageInput: HTMLInputElement) {
    const file: File = imageInput.files[0];
    const reader = new FileReader();

    reader.addEventListener('load', (event: any) => {
      this.selectedImage = event.target.result;
    });

    reader.readAsDataURL(file);
  }

  permissionsToString(): string {
    return this.selectedPlan && this.selectedPlan.permissions.length > 0 ? ('PERMISSIONS : ' + this.selectedPlan.permissions.map(p => p.name).join(', ')) : 'No permission set'
  }

  protected onSearchCompanyChange(search: string) {
    this.allCompaniesFiltered = this.allCompanies.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
  }

  addCompanyToAgency(company: Company) {
    this.apiCompany.linkAgency(company, this.createdCompany).then(() => {
      this.companyDataSource.data = this.companyDataSource.data.concat([company]);
    }).catch(err => {
      this.alert.notify('Error', err.error.message, 'error');
    }).finally(() => {
      this.selectedCompany = undefined;
    });
  }

  removeCompanyFromAgency(company: Company) {
    this.apiCompany.unlinkAgency(company, this.createdCompany).then(() => {
      this.companyDataSource.data = this.companyDataSource.data.filter(c => c.id !== company.id);
    }).catch(err => {
      this.alert.notify('Error', err.error.message, 'error');
    }).finally(() => {
      this.selectedCompany = undefined;
    });
  }

  protected companyToString(element?: Company): string {
    return element ? element.name : '';
  }

  private handleValidationErrors(error: any) {
    if (error.status === 422 && error.error?.errors) {
      if (error.error.errors.audiense_id) {
        this.companyFormGroup.get('audienseId').setErrors({ uniqueAudienseId: true });
        this.companyFormGroup.get('audienseId').markAsTouched();
      }
    } else {
      this.alert.notify('Error', 'An error occurred while saving the company', 'error');
    }
  }
}
